import type { CustomAttributeListOutput, TaskOutput } from "@orsane/schema/tasks";
import { ChevronDown, Hash, SquareDashed, SquareGanttChart, Timer, Type } from "lucide-react";
import { useChangeTaskCustomAttributeMutation } from "@/mutations/task-mutations";
import { useActiveTimer } from "@/queries/timer-queries";
import { OrbitSupportProjectSelect } from "../OrbitSupportProjectSelect";
import { NumberSelect } from "../property-selectors/NumberSelect";
import { SelectOneSelect } from "../property-selectors/SelectOneSelect";
import { TextSelect } from "../property-selectors/TextSelect";
import { TimerSelect } from "../property-selectors/TimerSelect";
import { DescriptionListItem, DescriptionListItemButton } from "./DescriptionList";

function EmptyToken() {
    return <span className="text-neutral-400">—</span>;
}
export const CustomAttributeValueDescriptionListItem = ({
    task,
    attribute,
}: {
    task: TaskOutput;
    attribute: CustomAttributeListOutput;
}) => {
    const { mutate } = useChangeTaskCustomAttributeMutation();
    const { data: activeTimer } = useActiveTimer();

    const customAttributeValue = task.customAttributeValues?.find((x) => x.id === attribute.id);

    switch (attribute.type) {
        case "timer":
            return (
                <DescriptionListItem label={attribute.name} icon={<Timer />}>
                    {/* The panel owns its own mutations: a timer's value is the sum of its
                        entries, not something written straight onto the task. */}
                    <TimerSelect
                        taskId={task.id}
                        customAttributeId={attribute.id as number}
                        value={customAttributeValue?.value ?? null}
                        runningSince={
                            activeTimer?.taskId === task.id && activeTimer?.customAttributeId === attribute.id
                                ? activeTimer.startedAt
                                : null
                        }
                        trigger={(content) => (
                            <DescriptionListItemButton>
                                {/* A timer's total is always defined — zero, not unset. */}
                                {content}
                            </DescriptionListItemButton>
                        )}
                    />
                </DescriptionListItem>
            );
        case "number":
            return (
                <DescriptionListItem label={attribute.name} icon={<Hash />}>
                    <NumberSelect
                        trigger={(content) => (
                            <DescriptionListItemButton>
                                {content}
                                {!content && <EmptyToken />}
                            </DescriptionListItemButton>
                        )}
                        value={customAttributeValue?.value}
                        onChange={(value) => {
                            mutate({
                                id: task.id,
                                customAttributeId: attribute.id,
                                value: value,
                            });
                        }}
                    />
                </DescriptionListItem>
            );
        case "text":
            return (
                <DescriptionListItem label={attribute.name} icon={<Type />}>
                    <TextSelect
                        trigger={(content, value) => (
                            <DescriptionListItemButton>
                                {!!value && <div className="truncate">{content}</div>}
                                {!value && <EmptyToken />}
                            </DescriptionListItemButton>
                        )}
                        value={customAttributeValue?.value}
                        onChange={(value) => {
                            mutate({
                                id: task.id,
                                customAttributeId: attribute.id,
                                value: value,
                            });
                        }}
                    />
                </DescriptionListItem>
            );
        case "select_one":
            return (
                <DescriptionListItem label={attribute.name} icon={<ChevronDown />}>
                    <SelectOneSelect
                        attributeId={attribute.id as number}
                        trigger={(content, value) => (
                            <DescriptionListItemButton>
                                {!!value && content}
                                {!value && <EmptyToken />}
                            </DescriptionListItemButton>
                        )}
                        value={customAttributeValue?.value}
                        onChange={(value) => {
                            mutate({
                                id: task.id,
                                customAttributeId: attribute.id,
                                value: value,
                            });
                        }}
                    />
                </DescriptionListItem>
            );
        case "orbit_support_project":
            return (
                <DescriptionListItem label={attribute.name} icon={<SquareGanttChart />}>
                    <OrbitSupportProjectSelect
                        trigger={(content, value) => (
                            <DescriptionListItemButton>
                                {!!value && content}
                                {!value && <EmptyToken />}
                            </DescriptionListItemButton>
                        )}
                        value={customAttributeValue?.value}
                        onChange={(value) => {
                            mutate({
                                id: task.id,
                                customAttributeId: attribute.id,
                                value: value,
                            });
                        }}
                    />
                </DescriptionListItem>
            );
        default:
            return (
                <DescriptionListItem label={attribute.name} icon={<SquareDashed />}>
                    <DescriptionListItemButton>{attribute.type}</DescriptionListItemButton>
                </DescriptionListItem>
            );
    }
};
